import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { AllClassesMobileCardViewProps } from "../interfaces/allClassesMobileCardViewProps";
import { CLASS_TYPE_UI } from "../utils/const";
import themestyle from "../theme";
import { CLASS_LIST_TEXT } from "../utils/const";
import CenteredLoader from "../components/CenteredLoader";
import { useQuery } from "@apollo/client";
import { ME } from "../graphql/query";

const AllClassesMobileCardView: React.FC<AllClassesMobileCardViewProps> = ({
  rows,
  loading = false,
  onView,
  onEdit,
  onDelete,
  onAttendance,
  paginationModel,
  onPaginationModelChange,
  rowCount,
  activeTab,
  canViewPreviousActions = false,
}) => {
  const { data: userData } = useQuery(ME, {
    fetchPolicy: "cache-first",
  });

  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  })();

  const userRole = userData?.me?.role?.toUpperCase() || storedUser?.role?.toUpperCase() || "";

  const totalPages = Math.ceil(rowCount / 3);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    onPaginationModelChange({
      page: value - 1,
      pageSize: 3,
    });
  };

  if (loading) {
    return <CenteredLoader />;
  }

  if (rows.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
          py: 4,
        }}
      >
        <Typography variant="body2" color="text.secondary">
           {CLASS_LIST_TEXT.NO_CLASSES}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", px: 1, py: 2, boxSizing: "border-box" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          mb: 3,
          width: "100%",
          maxWidth: "400px",
          mx: "auto",
        }}
      >
        {rows.slice(activeTab === "current" ? 0 : paginationModel.page * 3, activeTab === "current" ? rows.length : (paginationModel.page + 1) * 3).map((row) => {
          const classTypeConfig = CLASS_TYPE_UI[row.type];
          
          return (
            <Card
              key={row.id}
              sx={{
                borderRadius: "12px",
                border: "1px solid #E5E7EB",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                position: "relative",
                overflow: "visible",
                backgroundColor: themestyle.colors.white,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: "4px",
                  backgroundColor: themestyle.colors.activeGreen,
                  borderTopLeftRadius: "12px",
                  borderBottomLeftRadius: "12px",
                }}
              />

              <CardContent sx={{ pl: 2.5, pr: 1.5, py: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "16px",
                      fontWeight: 700,
                      color: "#1F2937",
                      flex: 1,
                      mr: 1,
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2, 
                      WebkitBoxOrient: "vertical",
                      textOverflow: "ellipsis",
                      wordBreak: "break-word",
                    }}
                  >
                    {row.instructor}
                  </Typography>
                  {activeTab === "previous" && (
                    <Typography
                      sx={{
                        color: row.sessionStatus === "Cancelled" ? "#B91C1C" : "#DC2626",
                        fontSize: "11px",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        mt: 0.5,
                        mr:1
                      }}
                    >
                      {row.sessionStatus || "Closed"}
                    </Typography>
                  )}
                </Box>
                <Box 
                  sx={{ 
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 1.5,
                    mb: 2 
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#6B7280",
                      flex: 1,
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2, 
                      WebkitBoxOrient: "vertical",
                      textOverflow: "ellipsis",
                      wordBreak: "break-word",
                    }}
                  >
                    {row.className}
                  </Typography>
                  {classTypeConfig && activeTab !== "previous" && (
                    <Box
                      sx={{
                        px: 1.2,
                        py: 0.4,
                        borderRadius: "8px",
                        bgcolor: classTypeConfig.bg,
                        color: classTypeConfig.color,
                        fontWeight: 600,
                        fontSize: "10px",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {classTypeConfig.label}
                    </Box>
                  )}
                </Box>
                
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                  <CalendarTodayOutlinedIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#6B7280",
                    }}
                  >
                    {row.mobileDate}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <AccessTimeOutlinedIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#6B7280",
                      }}
                    >
                      {row.mobileTime}
                    </Typography>
                  </Box>
                  
                  {(activeTab === "current" || activeTab === "upcoming" || activeTab === "previous") && (
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "8px",
                        bgcolor: "#F3F4F6",
                        border: "1px solid #E5E7EB",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 700,
                          color: "#1F2937",
                        }}
                      >
                        {row.enrollment}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {activeTab !== "previous" && (
                  <Box sx={{ display: "flex", gap: 0.5, ml: "auto", justifyContent: "flex-end" }}>
                    {userRole !== "INSTRUCTOR" ? (
                      <>
                        <IconButton
                          size="small"
                          sx={{
                            color: themestyle.colors.activeGreen,
                            width: "32px",
                            height: "32px",
                            "&:hover": {
                              bgcolor: "rgba(155,203,74,0.1)",
                            },
                          }}
                          onClick={() => onView && onView(row)}
                        >
                          <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
                        </IconButton>

                        <IconButton
                          size="small"
                          sx={{
                            color: "#1F2937",
                            width: "32px",
                            height: "32px",
                            "&:hover": {
                              bgcolor: "rgba(0,0,0,0.05)",
                            },
                          }}
                          onClick={() => onEdit && onEdit(row)}
                        >
                          <EditIcon sx={{ fontSize: 20 }} />
                        </IconButton>

                        <IconButton
                          size="small"
                          sx={{
                            color: themestyle.colors.error,
                            width: "32px",
                            height: "32px",
                            "&:hover": {
                              bgcolor: "rgba(229,57,53,0.1)",
                            },
                          }}
                          onClick={() => onDelete && onDelete(row)}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        {activeTab === "upcoming" && (
                          <IconButton
                            size="small"
                            sx={{
                              color: themestyle.colors.activeGreen,
                              width: "32px",
                              height: "32px",
                              "&:hover": {
                                bgcolor: "rgba(155,203,74,0.1)",
                              },
                            }}
                            onClick={() => onView && onView(row)}
                          >
                            <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                        )}
                      </>
                    )}
                    {activeTab !== "upcoming" && (
                      <IconButton
                        size="small"
                        sx={{
                          color: "#6B9080",
                          width: "32px",
                          height: "32px",
                        }}
                        onClick={() => onAttendance && onAttendance(row)}
                      >
                        <EventAvailableOutlinedIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    )}
                  </Box>
                )}
                {activeTab === "previous" && canViewPreviousActions && (
                  <Box sx={{ display: "flex", gap: 0.5, ml: "auto", justifyContent: "flex-end" }}>
                    <IconButton
                      size="small"
                      sx={{
                        color: "#374151",
                        width: "32px",
                        height: "32px",
                        "&:hover": {
                          bgcolor: "rgba(0,0,0,0.06)",
                        },
                      }}
                      onClick={() => onAttendance && onAttendance(row)}
                    >
                      <EventAvailableOutlinedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{
                        color: themestyle.colors.activeGreen,
                        width: "32px",
                        height: "32px",
                        "&:hover": {
                          bgcolor: "rgba(155,203,74,0.1)",
                        },
                      }}
                      onClick={() => onView && onView(row)}
                    >
                      <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>
      {totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            py: 1.5,
            px: 2,
            backgroundColor:themestyle.colors.white,
            borderRadius: "12px",
            border: "1px solid #E5E7EB",
            mt: 2,
            width: "100%",
            maxWidth: "400px",
            mx: "auto",
            boxSizing: "border-box",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: "14px",
              color: "#1F2937",
              fontWeight: 500,
              mr: 2,
            }}
          >
            {paginationModel.page * 3 + 1} - {Math.min((paginationModel.page + 1) * 3, rowCount)}
            <span style={{ marginLeft: "8px", marginRight: "8px", color: "#9CA3AF" }}>of</span>
            {rowCount}
          </Typography>

          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton
              size="small"
              onClick={(e) => handlePageChange(e, paginationModel.page)}
              disabled={paginationModel.page === 0}
              sx={{
                width: "32px",
                height: "32px",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                color: "#1F2937",
                "&:disabled": {
                  color: "#D1D5DB",
                  borderColor: "#F3F4F6",
                },
                "&:hover": {
                  backgroundColor: "#F9FAFB",
                },
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </IconButton>

            <IconButton
              size="small"
              onClick={(e) => handlePageChange(e, paginationModel.page + 2)}
              disabled={paginationModel.page >= totalPages - 1}
              sx={{
                width: "32px",
                height: "32px",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                color: "#1F2937",
                "&:disabled": {
                  color: "#D1D5DB",
                  borderColor: "#F3F4F6",
                },
                "&:hover": {
                  backgroundColor: "#F9FAFB",
                },
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};
export default AllClassesMobileCardView;
